import { baseApi } from './baseApi';
import {
  parsePlansResponse,
  parseCurrentSubscriptionResponse,
  parseCreateSubscriptionResponse,
  parseSubscriptionStatusResponse,
  parsePaymentHistoryResponse,
  parseSubscriptionErrorResponse
} from '../parsers/subscription.parsers';

const delay = (ms = 1000) => new Promise((resolve) => setTimeout(resolve, ms));

// Keep track of mock states locally for testing when VITE_ENABLE_MOCK is true
let mockCustomerType = 'REGULAR'; // 'REGULAR' | 'DIRECT'
let mockPollAttempts = 0;

export const subscriptionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // 1. Get Subscription Plans
    getPlans: builder.query({
      queryFn: async (args, api, extraOptions, baseQuery) => {
        if (import.meta.env.VITE_ENABLE_MOCK !== 'true') {
          try {
            const result = await baseQuery('/api/subscriptions/plans');
            if (result.error) return { error: parseSubscriptionErrorResponse(result.error) };
            return { data: parsePlansResponse(result.data) };
          } catch (err) {
            return { error: parseSubscriptionErrorResponse(err) };
          }
        }

        // Mock response
        await delay(500);
        return {
          data: {
            success: true,
            result: [
              {
                id: 1,
                name: 'Direct Plan',
                slug: 'direct',
                planCategory: 'DIRECT',
                subtitle: 'Advisory-led · Direct funds',
                description: 'Advisory-led · Direct funds',
                type: 'FIXED',
                amount: 399,
                currency: 'INR',
                billingCycle: 'monthly',
                features: [
                  'Access to Direct Funds — 0% distributor commission',
                  'More of your money stays invested & compounds over time',
                  'Expert, SEBI-registered investment advice'
                ],
                investmentPlanType: 'fee_based'
              }
            ]
          }
        };
      },
      providesTags: ['SubscriptionPlans']
    }),

    // 2. Fetch Current Active Subscription
    getCurrentSubscription: builder.query({
      queryFn: async (args, api, extraOptions, baseQuery) => {
        if (import.meta.env.VITE_ENABLE_MOCK !== 'true') {
          try {
            const result = await baseQuery('/api/subscriptions/current');
            if (result.error) {
              // Return standard error response
              return { error: parseSubscriptionErrorResponse(result.error) };
            }
            return { data: parseCurrentSubscriptionResponse(result.data) };
          } catch (err) {
            return { error: parseSubscriptionErrorResponse(err) };
          }
        }

        // Mock response based on customer type
        await delay(500);
        if (mockCustomerType !== 'DIRECT') {
          return {
            error: {
              status: 404,
              data: {
                code: 'SUB_404',
                message: 'No active subscription found for free tier'
              }
            }
          };
        }

        return {
          data: {
            success: true,
            result: {
              id: 482,
              status: 'ACTIVE',
              subscriptionType: 'FIXED',
              razorpaySubscriptionId: 'sub_PqL7eR8sT9uV',
              currentStart: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
              currentEnd: new Date(Date.now() + 25 * 24 * 60 * 60 * 1000).toISOString(), // 25 days later
              nextBillingAt: new Date(Date.now() + 25 * 24 * 60 * 60 * 1000).toISOString(),
              cancelAtCycleEnd: false,
              paidCount: 1,
              totalCount: 12,
              createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
              plan: {
                id: 1,
                name: 'Direct Plan',
                amount: 399,
                billingCycle: 'monthly',
                features: [
                  'Access to Direct Funds — 0% distributor commission',
                  'Expert, SEBI-registered investment advice'
                ]
              }
            }
          }
        };
      },
      providesTags: ['SubscriptionCurrent']
    }),

    // 3. Create Subscription
    createSubscription: builder.mutation({
      queryFn: async (payload, api, extraOptions, baseQuery) => {
        if (import.meta.env.VITE_ENABLE_MOCK !== 'true') {
          try {
            const result = await baseQuery({
              url: '/api/subscriptions/create',
              method: 'POST',
              body: payload
            });
            if (result.error) return { error: parseSubscriptionErrorResponse(result.error) };
            return { data: parseCreateSubscriptionResponse(result.data) };
          } catch (err) {
            return { error: parseSubscriptionErrorResponse(err) };
          }
        }

        // Mock response
        await delay(800);
        mockPollAttempts = 0; // reset poll attempts
        return {
          data: {
            success: true,
            result: {
              subscriptionId: 8901,
              razorpaySubscriptionId: 'sub_PqL7eR8sT9uV',
              shortUrl: 'https://rzp.io/i/sub_PqL7eR8sT9uV',
              planName: 'Direct Plan',
              amount: 399,
              billingCycle: 'monthly'
            }
          }
        };
      }
    }),

    // 4. Fetch Subscription Polling Status
    getSubscriptionStatus: builder.query({
      queryFn: async (razorpaySubscriptionId, api, extraOptions, baseQuery) => {
        if (import.meta.env.VITE_ENABLE_MOCK !== 'true') {
          try {
            const result = await baseQuery(`/api/subscriptions/status/${razorpaySubscriptionId}`);
            if (result.error) return { error: parseSubscriptionErrorResponse(result.error) };
            return { data: parseSubscriptionStatusResponse(result.data) };
          } catch (err) {
            return { error: parseSubscriptionErrorResponse(err) };
          }
        }

        // Mock polling logic
        await delay(500);
        mockPollAttempts += 1;

        if (mockPollAttempts < 3) {
          // Still processing
          return {
            data: {
              success: true,
              result: {
                status: 'CREATED',
                paymentStatus: 'pending',
                canClose: false,
                message: 'Verifying payment status...'
              }
            }
          };
        }

        // Simulating completion
        mockCustomerType = 'DIRECT';
        return {
          data: {
            success: true,
            result: {
              status: 'ACTIVE',
              paymentStatus: 'paid',
              canClose: true,
              message: 'Subscription activated successfully'
            }
          }
        };
      }
    }),

    // 5. Get Subscription Payment History
    getPaymentHistory: builder.query({
      queryFn: async (params = {}, api, extraOptions, baseQuery) => {
        if (import.meta.env.VITE_ENABLE_MOCK !== 'true') {
          try {
            const result = await baseQuery({
              url: '/api/subscriptions/payment-history',
              method: 'GET',
              params
            });
            if (result.error) return { error: parseSubscriptionErrorResponse(result.error) };
            return { data: parsePaymentHistoryResponse(result.data) };
          } catch (err) {
            return { error: parseSubscriptionErrorResponse(err) };
          }
        }

        // Mock payment history
        await delay(600);
        if (mockCustomerType !== 'DIRECT') {
          return {
            data: {
              success: true,
              result: {
                payments: [],
                total: 0,
                page: 1,
                limit: 20
              }
            }
          };
        }

        const mockPayments = [
          {
            id: 1201,
            paymentDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
            amount: 399,
            currency: 'INR',
            status: 'PAID',
            razorpayPaymentId: 'pay_PlK3mN4oP5qR',
            razorpayInvoiceId: 'inv_PzN7oP8qR9sT',
            failureReason: null,
            plan: {
              id: 1,
              name: 'Direct Plan',
              billingCycle: 'monthly'
            }
          },
          {
            id: 1102,
            paymentDate: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000).toISOString(),
            amount: 399,
            currency: 'INR',
            status: 'PAID',
            razorpayPaymentId: 'pay_MhT5nO8qP9rS',
            razorpayInvoiceId: 'inv_JkF8pQ2sT4uW',
            failureReason: null,
            plan: {
              id: 1,
              name: 'Direct Plan',
              billingCycle: 'monthly'
            }
          }
        ];

        return {
          data: {
            success: true,
            result: {
              payments: mockPayments,
              total: mockPayments.length,
              page: params.page || 1,
              limit: params.limit || 20
            }
          }
        };
      },
      providesTags: ['SubscriptionPaymentHistory']
    }),

    // Helper to manually set mock customer type (for development testing)
    setMockCustomerType: builder.mutation({
      queryFn: async (customerType) => {
        mockCustomerType = customerType;
        return { data: { success: true, customerType } };
      },
      invalidatesTags: ['SubscriptionCurrent', 'SubscriptionPaymentHistory', 'Customer']
    })
  })
});

export const {
  useGetPlansQuery,
  useGetCurrentSubscriptionQuery,
  useLazyGetCurrentSubscriptionQuery,
  useCreateSubscriptionMutation,
  useLazyGetSubscriptionStatusQuery,
  useGetPaymentHistoryQuery,
  useSetMockCustomerTypeMutation
} = subscriptionApi;
